import { Component } from '@theme/component';
import { updateAllHeaderCustomProperties } from '@theme/utilities';

const DISMISSED_STORAGE_KEY = 'dismissed_announcements';
const ATTRIBUTION_STORAGE_KEY = 'attribution_data';

const PATH_TIER_ORDER = /** @type {const} */ ({
  none: 0,
  global: 1,
  wildcard: 2,
  exact: 3,
});

/**
 * @typedef {'exact' | 'wildcard' | 'global' | 'none'} PathTier
 */

/**
 * @typedef {object} AnnouncementConfig
 * @property {string} handle
 * @property {string} contentHtml
 * @property {string} contentMobileHtml
 * @property {string} buttonLabel
 * @property {string} buttonUrl
 * @property {string} backgroundColor
 * @property {string} textColor
 * @property {string} targetPagesRaw
 * @property {string} utmTriggersRaw
 */

/**
 * @param {unknown} raw
 * @returns {string[]}
 */
function parseTargetPages(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((p) => String(p).trim()).filter(Boolean);
  }
  const s = String(raw).trim();
  if (!s) return [];
  if (s.startsWith('[')) {
    try {
      const v = JSON.parse(s);
      if (Array.isArray(v)) return v.map((p) => String(p).trim()).filter(Boolean);
    } catch {
      return s.split(',').map((p) => p.trim()).filter(Boolean);
    }
  }
  return s.split(',').map((p) => p.trim()).filter(Boolean);
}

/**
 * @param {unknown} raw
 * @returns {Record<string, string | string[]> | null}
 */
function parseUtmTriggers(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    const keys = Object.keys(/** @type {object} */ (raw));
    return keys.length ? /** @type {Record<string, string | string[]>} */ (raw) : null;
  }
  const s = String(raw).trim();
  if (!s) return null;
  try {
    let v = JSON.parse(s);
    if (typeof v === 'string') {
      const inner = v.trim();
      if (inner.startsWith('{') || inner.startsWith('[')) {
        try {
          v = JSON.parse(inner);
        } catch {
          /* keep outer string */
        }
      }
    }
    if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length) {
      return /** @type {Record<string, string | string[]>} */ (v);
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * @param {string} path
 */
function normalizePath(path) {
  if (!path) return '/';
  let p = path.split('?')[0] ?? '/';
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

/**
 * @param {PathTier | null} current
 * @param {PathTier} next
 * @returns {PathTier}
 */
function rankPathTier(current, next) {
  if (!current || next === 'none') return current ?? next;
  if (PATH_TIER_ORDER[next] > PATH_TIER_ORDER[/** @type {PathTier} */ (current)]) return next;
  return /** @type {PathTier} */ (current);
}

/**
 * @param {string} currentPath
 * @param {string[]} patterns
 * @returns {{ ok: boolean, tier: PathTier }}
 */
function evaluatePathTargeting(currentPath, patterns) {
  const normalized = normalizePath(currentPath);
  if (!patterns.length) {
    return { ok: true, tier: 'global' };
  }

  /** @type {PathTier | null} */
  let best = null;

  for (const rawPattern of patterns) {
    const p = normalizePath(String(rawPattern).trim());
    if (!p || p === '*') {
      best = rankPathTier(best, 'global');
      continue;
    }
    if (p.endsWith('/*')) {
      const base = p.slice(0, -2);
      if (normalized === base || normalized.startsWith(`${base}/`)) {
        best = rankPathTier(best, 'wildcard');
      }
    } else if (normalized === p) {
      best = rankPathTier(best, 'exact');
    }
  }

  const tier = best ?? 'none';
  return { ok: tier !== 'none', tier };
}

/**
 * @param {Record<string, string>} params
 * @param {string} key
 */
function lookupParam(params, key) {
  const lower = key.toLowerCase();
  for (const [pk, pv] of Object.entries(params)) {
    if (pk.toLowerCase() === lower && pv != null && String(pv).length > 0) {
      return String(pv);
    }
  }
  return '';
}

/**
 * @param {string} pattern
 * @param {string} value
 */
function valueMatchesPattern(pattern, value) {
  const p = pattern.trim();
  const v = value.trim();
  if (p === '*') return v.length > 0;
  if (!p.includes('*')) return p.toLowerCase() === v.toLowerCase();
  const parts = p.split('*').map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`^${parts.join('.*')}$`, 'i');
  return re.test(v);
}

/**
 * @param {string | string[]} rule
 * @param {string} actual
 */
function valueMatchesRule(rule, actual) {
  if (Array.isArray(rule)) {
    return rule.some((r) => valueMatchesPattern(String(r), actual));
  }
  return valueMatchesPattern(String(rule), actual);
}

/**
 * @param {Record<string, string | string[]> | null} triggers
 * @param {Record<string, string>} params
 */
function utmTriggersMatch(triggers, params) {
  if (!triggers) return true;
  const keys = Object.keys(triggers);
  if (!keys.length) return true;
  for (const key of keys) {
    const expected = triggers[key];
    if (expected == null) return false;
    const actual = lookupParam(params, key);
    if (!valueMatchesRule(expected, actual)) {
      return false;
    }
  }
  return true;
}

/**
 * @returns {Record<string, string>}
 */
function getLocalStorageAttributionParamRecord() {
  /** @type {Record<string, string>} */
  const out = {};
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      for (const [k, v] of Object.entries(parsed)) {
        const trimmed = typeof v === 'string' ? v.trim() : String(v ?? '').trim();
        if (trimmed) out[k] = trimmed;
      }
    }
  } catch {
    /* ignore */
  }
  return out;
}

/**
 * URL query values win over `localStorage` for the same key (lets a new `?utm_*=`
 * link override a prior session).
 *
 * @param {Record<string, string>} storedParams
 * @param {Record<string, string>} urlParams
 * @returns {Record<string, string>}
 */
function mergeUrlOverStoredRecord(storedParams, urlParams) {
  return { ...storedParams, ...urlParams };
}

/**
 * Current page query string only.
 *
 * @returns {Record<string, string>}
 */
function getUrlSearchParamRecord() {
  /** @type {Record<string, string>} */
  const out = {};
  const sp = new URLSearchParams(window.location.search);
  for (const [k, v] of sp.entries()) {
    const trimmed = String(v).trim();
    if (trimmed) out[k] = trimmed;
  }
  return out;
}

/**
 * @returns {string[]}
 */
function getDismissedHandles() {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

/**
 * @param {string} handle
 */
function persistDismissed(handle) {
  const list = new Set(getDismissedHandles());
  list.add(handle);
  try {
    window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...list]));
  } catch {
    /* ignore */
  }
}

/**
 * @param {PathTier} pathTier
 * @param {boolean} utmTriggered
 * @param {boolean} utmMatched
 */
function scoreAnnouncement(pathTier, utmTriggered, utmMatched) {
  if (utmTriggered && !utmMatched) return -1;
  if (!utmTriggered) {
    if (pathTier === 'exact') return 100;
    if (pathTier === 'wildcard') return 50;
    return 0;
  }
  if (pathTier === 'exact') return 300;
  if (pathTier === 'wildcard') return 250;
  return 200;
}

/**
 * @param {Record<string, string | string[]> | null} triggers
 */
function utmTriggerKeyCount(triggers) {
  if (!triggers) return 0;
  return Object.keys(triggers).length;
}

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {(() => void) | undefined} */
  #metaobjectTeardown = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('data-metaobject-mode')) {
      this.#initMetaobjectMode();
      return;
    }

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    this.suspend();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#metaobjectTeardown?.();
    this.#metaobjectTeardown = undefined;
    super.disconnectedCallback();
  }

  #initMetaobjectMode() {
    const script = this.querySelector('script[type="application/json"][data-announcement-configs]');
    const inner = this.querySelector('[data-metaobject-root]');
    if (!(script instanceof HTMLScriptElement) || !(inner instanceof HTMLElement)) {
      return;
    }

    const aside = this.closest('[data-announcement-aside]');
    const prev = aside?.previousElementSibling;
    const bg =
      prev instanceof HTMLElement && prev.hasAttribute('data-announcement-background')
        ? prev
        : aside?.parentElement?.querySelector('[data-announcement-background]');
    // Auto mode renders the theme editor blocks as a visible sibling; the
    // metaobject slot only replaces them when an entry matches. "Metaobject
    // only" has no fallback, so the bar stays hidden until something matches.
    const fallback = aside?.querySelector('[data-announcement-blocks-fallback]');

    const showFallback = () => {
      inner.hidden = true;
      // Hide the whole slot, not just its inner: the slider classes set
      // display: flex, so a hidden inner would still leave an empty flex item
      // sharing the aside with the blocks.
      this.hidden = Boolean(fallback instanceof HTMLElement);
      if (aside instanceof HTMLElement) {
        aside.style.removeProperty('--announcement-bar-background');
        aside.style.removeProperty('--announcement-bar-foreground');
      }
      if (bg instanceof HTMLElement) {
        bg.style.removeProperty('background-color');
      }
      if (fallback instanceof HTMLElement) {
        fallback.hidden = false;
        if (fallback instanceof AnnouncementBar) fallback.resume();
        aside?.classList.remove('announcement-bar--metaobject-pending');
        bg?.classList.remove('announcement-bar--metaobject-pending');
      } else {
        aside?.classList.add('announcement-bar--metaobject-pending');
        bg?.classList.add('announcement-bar--metaobject-pending');
      }
      window.requestAnimationFrame(() => {
        updateAllHeaderCustomProperties();
      });
    };

    /** @type {AnnouncementConfig[]} */
    let configs = [];
    try {
      configs = JSON.parse(script.textContent || '[]');
    } catch {
      configs = [];
    }

    if (!Array.isArray(configs) || configs.length === 0) {
      showFallback();
      return;
    }

    const currentPath = normalizePath(window.location.pathname || this.dataset.currentPath || '/');
    const urlParams = getUrlSearchParamRecord();
    const storedParams = getLocalStorageAttributionParamRecord();
    const mergedMatchParams = mergeUrlOverStoredRecord(storedParams, urlParams);
    const dismissed = new Set(getDismissedHandles());

    /** @type {{ score: number; config: AnnouncementConfig; pathTier: PathTier; triggerKeyCount: number; originalIndex: number }[]} */
    const candidates = [];

    // Single pass: an entry with `utm_triggers` is a candidate only when every
    // trigger key matches the visitor (URL over stored attribution). An entry
    // with no triggers is always a candidate and acts as the fallback — its
    // path-only score (0–100) is always below a matched campaign entry
    // (200–300), so campaigns win when they match and the default shows otherwise.
    configs.forEach((config, originalIndex) => {
      if (!config || typeof config.handle !== 'string') return;
      if (dismissed.has(config.handle)) return;

      const patterns = parseTargetPages(config.targetPagesRaw);
      const pathResult = evaluatePathTargeting(currentPath, patterns);
      if (!pathResult.ok) return;

      const triggers = parseUtmTriggers(config.utmTriggersRaw);
      const utmTriggered = Boolean(triggers && Object.keys(triggers).length > 0);
      const utmMatched = utmTriggersMatch(triggers, mergedMatchParams);
      const score = scoreAnnouncement(pathResult.tier, utmTriggered, utmMatched);
      if (score < 0) return;
      candidates.push({
        score,
        config,
        pathTier: pathResult.tier,
        triggerKeyCount: utmTriggerKeyCount(triggers),
        originalIndex,
      });
    });

    if (!candidates.length) {
      showFallback();
      return;
    }

    candidates.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const tierDiff = PATH_TIER_ORDER[b.pathTier] - PATH_TIER_ORDER[a.pathTier];
      if (tierDiff !== 0) return tierDiff;
      if (b.triggerKeyCount !== a.triggerKeyCount) return b.triggerKeyCount - a.triggerKeyCount;
      return b.originalIndex - a.originalIndex;
    });
    const winner = candidates[0]?.config;
    if (!winner) {
      showFallback();
      return;
    }

    const desktopEl = inner.querySelector('.announcement-bar__content--desktop');
    const mobileEl = inner.querySelector('.announcement-bar__content--mobile');
    const ctaTemplate = this.querySelector('template[data-announcement-cta-template]');
    let cta = this.querySelector('[data-announcement-cta]');
    const dismissBtn = this.querySelector('[data-announcement-dismiss]');

    if (desktopEl instanceof HTMLElement) {
      desktopEl.innerHTML = winner.contentHtml || '';
    }
    if (mobileEl instanceof HTMLElement) {
      const mobileHtml = winner.contentMobileHtml?.trim() ? winner.contentMobileHtml : winner.contentHtml;
      mobileEl.innerHTML = mobileHtml || '';
      if (!winner.contentMobileHtml?.trim()) {
        inner.dataset.desktopOnly = 'true';
      } else {
        delete inner.dataset.desktopOnly;
      }
    }

    if (winner.backgroundColor) {
      if (aside instanceof HTMLElement) {
        aside.style.setProperty('--announcement-bar-background', winner.backgroundColor);
      }
      if (bg instanceof HTMLElement) {
        bg.style.backgroundColor = winner.backgroundColor;
      }
    }
    if (winner.textColor && aside instanceof HTMLElement) {
      aside.style.setProperty('--announcement-bar-foreground', winner.textColor);
    }

    // The CTA lives in a <template> until there is a real label + URL, so the
    // page never carries an href-less placeholder <a> (announced as a dead link).
    const label = (winner.buttonLabel || '').trim();
    const url = (winner.buttonUrl || '').trim();
    if (label && url) {
      if (!(cta instanceof HTMLAnchorElement) && ctaTemplate instanceof HTMLTemplateElement) {
        const clone = ctaTemplate.content.firstElementChild?.cloneNode(true);
        if (clone instanceof HTMLAnchorElement) {
          ctaTemplate.before(clone);
          cta = clone;
        }
      }
      if (cta instanceof HTMLAnchorElement) {
        cta.textContent = label;
        cta.href = url;
        try {
          const abs = new URL(url, window.location.origin);
          if (abs.origin !== window.location.origin) {
            cta.target = '_blank';
            cta.rel = 'noopener noreferrer';
          } else {
            cta.removeAttribute('target');
            cta.removeAttribute('rel');
          }
        } catch {
          cta.removeAttribute('target');
          cta.removeAttribute('rel');
        }
      }
    } else {
      cta?.remove();
    }

    if (fallback instanceof HTMLElement) {
      fallback.hidden = true;
      if (fallback instanceof AnnouncementBar) fallback.suspend();
    }
    this.hidden = false;
    inner.hidden = false;
    if (aside instanceof HTMLElement) {
      aside.classList.remove('announcement-bar--metaobject-pending');
    }
    if (bg instanceof HTMLElement) {
      bg.classList.remove('announcement-bar--metaobject-pending');
    }

    const onDismiss = () => {
      persistDismissed(winner.handle);
      if (desktopEl instanceof HTMLElement) desktopEl.innerHTML = '';
      if (mobileEl instanceof HTMLElement) mobileEl.innerHTML = '';
      cta?.remove();
      showFallback();
    };

    if (dismissBtn instanceof HTMLButtonElement) {
      dismissBtn.addEventListener('click', onDismiss);
      this.#metaobjectTeardown = () => dismissBtn.removeEventListener('click', onDismiss);
    }

    window.requestAnimationFrame(() => {
      updateAllHeaderCustomProperties();
    });
  }

  /**
   * Arrow buttons (on:click="/next" / "/previous"). The bar is a polite live
   * region only for these user-initiated changes; autoplay switches it off so a
   * rotating bar never interrupts screen-reader reading (WCAG 2.2.2 / 4.1.3).
   */
  next() {
    this.setAttribute('aria-live', 'polite');
    this.current += 1;
  }

  previous() {
    this.setAttribute('aria-live', 'polite');
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.setAttribute('aria-live', 'off');
      this.current += 1;
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * Sync aria-hidden / inert / tabindex so hidden slides never contain tabbable
   * controls, and the visible slide is interactive (WCAG 4.1.2 aria-hidden-focus).
   * Static HTML sets inert + tabindex="-1" on non-first slides; this must undo
   * those when a slide becomes active (autoplay / next / previous).
   * @param {HTMLElement} slide
   * @param {boolean} isVisible
   */
  #setSlideVisibility(slide, isVisible) {
    slide.setAttribute('aria-hidden', `${!isVisible}`);
    slide.toggleAttribute('inert', !isVisible);

    const focusable = slide.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
    );

    for (const el of focusable) {
      if (!(el instanceof HTMLElement)) continue;

      if (!isVisible) {
        if (!el.hasAttribute('data-slideshow-tabindex')) {
          const currentTabIndex = el.getAttribute('tabindex');
          // Preserve only intentional non-negative tabindex. SSR may set -1 on
          // hidden slides; treat that as "natively focusable when shown".
          el.setAttribute(
            'data-slideshow-tabindex',
            currentTabIndex != null && currentTabIndex !== '-1' ? currentTabIndex : ''
          );
        }
        el.setAttribute('tabindex', '-1');
        continue;
      }

      if (el.hasAttribute('data-slideshow-tabindex')) {
        const original = el.getAttribute('data-slideshow-tabindex');
        el.removeAttribute('data-slideshow-tabindex');
        if (original === '' || original == null) {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', original);
        }
      } else if (el.getAttribute('tabindex') === '-1') {
        // First time this slide is shown: clear SSR tabindex="-1"
        el.removeAttribute('tabindex');
      }
    }
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    for (const [index, slide] of slides.entries()) {
      this.#setSlideVisibility(slide, index === relativeIndex);
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
